import { buildAgentApiUrl } from "@/lib/api-config";
import type {
  AuthProviderCredentialKind,
  AuthProviderDescriptor,
  AuthResponse,
} from "@/lib/auth-types";

async function parseError(response: Response) {
  try {
    const payload = await response.clone().json();
    if (typeof payload === "string") {
      return payload;
    }

    if (payload?.message) {
      return payload.message as string;
    }

    if (payload?.detail) {
      return payload.detail as string;
    }

    if (payload?.title) {
      return payload.title as string;
    }

    if (payload?.errors) {
      const firstError = Object.values(payload.errors)[0] as string[] | undefined;
      if (firstError?.length) {
        return firstError[0];
      }
    }

    return `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

// Fallback error parser lives in api-client but we duplicate only what we need here.
async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(buildAgentApiUrl(path), {
    cache: "no-store",
    ...init,
    headers,
  });

  if (!response.ok) {
    const message = await parseError(response);
    throw new Error(message);
  }

  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

type RawAuthProviderDescriptor = Omit<AuthProviderDescriptor, "credentialKind"> & {
  credentialKind: string | number;
};

function normalizeCredentialKind(value: string | number): AuthProviderCredentialKind {
  if (typeof value === "number") {
    // Wire values: 0 = IdToken, 1 = AuthorizationCode.
    return value >= 1 ? "AuthorizationCode" : "IdToken";
  }

  const normalized = value.toLowerCase();
  if (normalized === "authorizationcode" || normalized === "authorization_code") {
    return "AuthorizationCode";
  }
  return "IdToken";
}

function normalizeDescriptor(raw: RawAuthProviderDescriptor): AuthProviderDescriptor {
  return {
    ...raw,
    credentialKind: normalizeCredentialKind(raw.credentialKind),
    metadata: raw.metadata ?? {},
  };
}

export async function fetchAuthProviders(): Promise<AuthProviderDescriptor[]> {
  const providers = await requestJson<RawAuthProviderDescriptor[]>("/api/auth/providers");
  return providers.map(normalizeDescriptor);
}

export type AuthExchangeRequest = {
  credential?: string;
  code?: string;
  state?: string;
  inviteCode?: string;
  /**
   * Self-signup (docs/plan/stripe-subscriptions.md, "Sign-up"): when billing is
   * enabled and the principal matches no account, the API creates a new user
   * who then creates an organization and picks a plan. Ignored when billing
   * is disabled.
   */
  signup?: boolean;
};

export async function exchangeAuthToken(
  provider: string,
  payload: AuthExchangeRequest,
): Promise<AuthResponse> {
  return requestJson<AuthResponse>(`/api/auth/${provider}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type AuthorizeResponse = {
  authorizeUrl: string;
};

/**
 * sessionStorage key used to carry the post-login `returnTo` destination across a
 * redirect-based OAuth round-trip (the provider redirect can't preserve our app state).
 */
export const OAUTH_RETURN_TO_KEY = "scirix.oauth.returnTo";

/**
 * sessionStorage key used to carry a one-time invite code across the same OAuth
 * round-trip; the callback page attaches it to the exchange so the API can
 * provision a first-time user (including emailless SSO principals).
 */
export const OAUTH_INVITE_CODE_KEY = "scirix.oauth.inviteCode";

/**
 * sessionStorage key carrying the "create an organization" intent across the
 * OAuth round-trip. Set by the login page when the user chose sign-up; the
 * callback page reads it once and sends `signup: true` on the exchange.
 */
export const OAUTH_SIGNUP_KEY = "scirix.oauth.signup";

/**
 * Begins a redirect-based (AuthorizationCode) login: asks the API for the provider's
 * authorize URL. The caller navigates the browser there; the provider redirects back to
 * /oauth/login/{provider}/callback, which posts the code + state to {@link exchangeAuthToken}.
 */
export async function beginAuthorize(provider: string): Promise<AuthorizeResponse> {
  return requestJson<AuthorizeResponse>(`/api/auth/${provider}/authorize`, {
    method: "POST",
  });
}
